import {
  faCheckCircle,
  faList,
  faShareSquare
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ButtonLink from 'components/shared/ButtonLink.styled';
import { LoggedUserContext } from 'context/logged-user/loggeduser.context';
import WIPFeaturesContext from 'context/wip-features/wip-features.context';
import { DEFAULT_FILTER } from 'features/job-offers/list/components/JobOffersListComponent.constants';
import { useI18n } from 'hooks/useI18n';
import { UIOfferFooter } from 'interfaces/ui/ui-ofer-footer';
import { useRouter } from 'next/dist/client/router';
import Link from 'next/link';
import { useContext, useEffect, useRef, useState } from 'react';
import ServiceEventTracking from 'services/service.event-tracking';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { getUrlParamsFromJobOffersFilter } from 'utils/job-offers-filter';
import { getLocale } from 'utils/localize.utils';
import Avatar from '../../shared/Avatar';
import { Button } from '../../shared/Button';
import {
  StyledOfferFooter,
  StyledOfferFooterActions,
  StyledOfferFooterContainer,
  StyledOfferFooterLogo,
  StyledOfferFooterScout,
  StyledOfferFooterScoutLabel,
  StyledOfferFooterScoutName,
  StyledOfferFooterScoutTitle,
  StyledOfferFooterText,
  StyledOfferFooterTitle
} from './OfferFooter.styled';

const OfferFooter: React.FC<UIOfferFooter> = ({
  id,
  logo,
  company,
  scout,
  scoutAvatar,
  scoutUrl,
  openShareModal,
  handleApplyClick,
  internalCode,
  closed,
  userApplied
}) => {
  const { t } = useI18n('job-offer');
  const { locale } = useRouter();
  const { user } = useContext(LoggedUserContext);
  const showWIPFeatures = useContext(WIPFeaturesContext);

  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const returnUrl = `/${locale}${getAppUrl(
    APP_URLS.portalV2Dashboard,
    locale
  )}`;

  const trackApplyClick = (): void => {
    ServiceEventTracking.track({
      type: 'Offers - Offer Interested',
      properties: {
        buttonName: 'footer',
        offerCompany: company,
        offerName: internalCode,
        offerURL: window.location.href,
        pageLanguage: locale
      }
    });
  };

  useEffect(() => {
    if (imgRef.current?.complete) {
      setLoaded(true);
    }
  }, [imgRef]);

  useEffect(() => {
    if (loaded) {
      window.dispatchEvent(new Event('resize'));
    }
  }, [loaded]);

  return (
    <StyledOfferFooter>
      <StyledOfferFooterContainer>
        {logo && (
          <StyledOfferFooterLogo>
            <img
              ref={imgRef}
              src={logo}
              alt={company}
              onLoad={() => {
                setLoaded(true);
              }}
            />
          </StyledOfferFooterLogo>
        )}

        {!closed && (
          <StyledOfferFooterTitle>
            {t('footer.convinced')}
          </StyledOfferFooterTitle>
        )}

        {closed && (
          <StyledOfferFooterTitle>{t('footer.closed')}</StyledOfferFooterTitle>
        )}

        {closed && (
          <StyledOfferFooterActions>
            {showWIPFeatures && (
              <Button variant="secondaryInverse" sizes="lg">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>{t('footer.register')}</span>
              </Button>
            )}

            <Link
              href={`${getAppUrl(
                APP_URLS.jobOffers,
                locale
              )}?${getUrlParamsFromJobOffersFilter(
                DEFAULT_FILTER,
                getLocale(locale)
              )}`}
              passHref
            >
              <ButtonLink variant="secondaryInverse" sizes="lg">
                <FontAwesomeIcon icon={faList} />
                <span>{t('footer.viewMoreOffers')}</span>
              </ButtonLink>
            </Link>
          </StyledOfferFooterActions>
        )}

        {!closed && (
          <StyledOfferFooterActions>
            {!userApplied && (
              <ButtonLink
                variant="brandTalent"
                sizes="lg"
                id="footer-interested-button"
                className="interested-button"
                href={
                  !user
                    ? `${getAppUrl(
                        APP_URLS.join,
                        locale
                      )}?locale=${locale}&offerId=${id}&source=footer-job&returnUrl=${returnUrl}`
                    : '#'
                }
                onClick={(e) => {
                  e.preventDefault();

                  trackApplyClick();
                  ServiceEventTracking.trackLandingCtaClicked('footer-job');

                  if (user && !!handleApplyClick) {
                    handleApplyClick(e);
                  }

                  if (!user) {
                    // Delay the redirect to give some time to the tracking to complete
                    setTimeout(() => {
                      const url = `${
                        window.location.origin
                      }/${locale}${getAppUrl(
                        APP_URLS.join,
                        locale
                      )}?locale=${locale}&offerId=${id}&source=footer-job&returnUrl=${returnUrl}`;

                      window.location.replace(url);
                    }, 300);
                  }
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>{t('footer.interested')}</span>
              </ButtonLink>
            )}

            <Button
              variant="tertiaryInverse"
              sizes="lg"
              onClick={openShareModal}
            >
              <FontAwesomeIcon icon={faShareSquare} />
              <span>{t('footer.share')}</span>
            </Button>
          </StyledOfferFooterActions>
        )}
        {!closed && (
          <StyledOfferFooterText
            dangerouslySetInnerHTML={{ __html: t('footer.manfred_text') }}
          ></StyledOfferFooterText>
        )}

        {closed && (
          <StyledOfferFooterText
            dangerouslySetInnerHTML={{
              __html: t('footer.manfred_text_closed')
            }}
          ></StyledOfferFooterText>
        )}
        {scout && (
          <div>
            <StyledOfferFooterScoutTitle>
              {t('footer.needHelp')}
            </StyledOfferFooterScoutTitle>
            <StyledOfferFooterScout>
              <Avatar avatarImg={scoutAvatar} avatarAlt={scout} />
              <div>
                <StyledOfferFooterScoutName
                  href={`mailto:${scoutUrl}`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {scout}
                </StyledOfferFooterScoutName>
                <StyledOfferFooterScoutLabel>
                  {t('footer.scout')}
                </StyledOfferFooterScoutLabel>
              </div>
            </StyledOfferFooterScout>
          </div>
        )}
      </StyledOfferFooterContainer>
    </StyledOfferFooter>
  );
};

export default OfferFooter;
